import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";
import { formatMoney, formatDate } from "@/modules/config";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";

export function GlobalSearch() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (!open) {
      setQ("");
      setResults([]);
    }
  }, [open]);

  useEffect(() => {
    const query = q.trim();
    if (query.length < 2) {
      setResults([]);
      return;
    }
    setLoading(true);
    const handle = setTimeout(async () => {
      try {
        const { data } = await api.get("/search", { params: { q: query } });
        setResults(data.results || []);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(handle);
  }, [q]);

  const go = (r) => {
    setOpen(false);
    navigate(r.path);
  };

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid="global-search-btn"
        className="hidden sm:flex items-center gap-2 border border-slate-300 bg-white rounded-md px-3 py-1.5 text-sm text-slate-400 hover:border-blue-400 hover:text-slate-500 transition-colors duration-150 w-52">
        <Search className="h-4 w-4" />
        <span className="flex-1 text-left">{t("Ara...")}</span>
        <kbd className="text-[10px] text-slate-400 border border-slate-200 rounded px-1">Ctrl+K</kbd>
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg p-0 overflow-hidden gap-0" data-testid="global-search-dialog">
          <DialogTitle className="sr-only">{t("Global Arama")}</DialogTitle>
          <DialogDescription className="sr-only">{t("Firma, çek no, açıklama ara...")}</DialogDescription>
          <div className="flex items-center gap-2 border-b border-slate-200 px-4">
            <Search className="h-4 w-4 text-slate-400 shrink-0" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
              placeholder={t("Firma, çek no, açıklama ara...")}
              data-testid="global-search-input"
              className="flex-1 h-12 bg-transparent outline-none text-sm text-[#0A1128]" />
            {loading && <Loader2 className="h-4 w-4 animate-spin text-blue-600 shrink-0" />}
          </div>
          <div className="max-h-80 overflow-y-auto p-2">
            {q.trim().length === 1 && (
              <p className="p-4 text-xs text-slate-400">{t("En az 2 karakter yazın")}</p>
            )}
            {q.trim().length >= 2 && !loading && results.length === 0 && (
              <p className="p-4 text-xs text-slate-400" data-testid="search-empty">{t("Sonuç bulunamadı")}</p>
            )}
            {results.map((r) => (
              <button key={`${r.module}-${r.id}`} type="button" onClick={() => go(r)}
                data-testid={`search-result-${r.id}`}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md hover:bg-slate-50 text-left transition-colors duration-150">
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-[#0A1128] truncate">{r.title}</p>
                  <p className="text-[11px] text-slate-400">
                    {t(r.module)}{r.date ? ` · ${formatDate(r.date)}` : ""}
                  </p>
                </div>
                {r.amount != null && (
                  <span className="text-sm font-semibold tabular text-[#0A1128]">{formatMoney(r.amount)}</span>
                )}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
